import createCache, { type EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalali';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { faIR } from '@mui/x-date-pickers/locales';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { ColorModeContext, readColorMode, writeColorMode } from './colorMode';
import { createPhoenixTheme, type ColorMode, type Density } from './createPhoenixTheme';
import './fonts.css';

const caches: Partial<Record<'rtl' | 'ltr', EmotionCache>> = {};

/** Emotion cache با stylis-plugin-rtl (قواعد CSS در RTL قرینه می‌شوند). */
export function getEmotionCache(direction: 'rtl' | 'ltr'): EmotionCache {
  const existing = caches[direction];
  if (existing) {
    return existing;
  }
  const cache =
    direction === 'rtl'
      ? createCache({ key: 'phx-rtl', stylisPlugins: [prefixer, rtlPlugin] })
      : createCache({ key: 'phx-ltr', stylisPlugins: [prefixer] });
  caches[direction] = cache;
  return cache;
}

export interface PhoenixThemeProviderProps {
  children: ReactNode;
  /**
   * روشن، تیره، یا پیروی از سیستم‌عامل.
   * اگر نباشد، انتخاب ذخیره‌شده و در نبود آن روشن است.
   */
  mode?: ColorMode | 'system';
  density?: Density;
  direction?: 'rtl' | 'ltr';
  /** زبان سند (fa پیش‌فرض) برای lang و dir روی html. */
  language?: string;
}

/** Theme + RTL + DatePicker شمسی (date-fns-jalali) برای کل اپ. */
export function PhoenixThemeProvider({
  children,
  mode,
  density = 'compact',
  direction = 'rtl',
  language = 'fa',
}: PhoenixThemeProviderProps) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [choice, setChoice] = useState<ColorMode>(() => (mode === 'light' || mode === 'dark' ? mode : readColorMode()));
  const resolvedMode: ColorMode = mode === 'system' ? (prefersDark ? 'dark' : 'light') : choice;
  const theme = useMemo(
    () => createPhoenixTheme({ mode: resolvedMode, density, direction }),
    [resolvedMode, density, direction],
  );
  const colorMode = useMemo(
    () => ({
      mode: resolvedMode,
      setMode: (next: ColorMode) => {
        writeColorMode(next);
        setChoice(next);
      },
    }),
    [resolvedMode],
  );

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('data-color-mode', resolvedMode);
    document.documentElement.style.colorScheme = resolvedMode;
  }, [direction, language, resolvedMode]);

  return (
    <CacheProvider value={getEmotionCache(direction)}>
      <ThemeProvider theme={theme}>
        <ColorModeContext.Provider value={colorMode}>
        <LocalizationProvider
          dateAdapter={AdapterDateFnsJalali}
          localeText={faIR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <CssBaseline />
          {children}
        </LocalizationProvider>
        </ColorModeContext.Provider>
      </ThemeProvider>
    </CacheProvider>
  );
}
